import type { FC, PropsWithChildren } from "react";
import React, { createContext, useContext, useMemo } from "react";

import type { ITextCore } from "../types";

type TextCoreContextValue = Pick<
    ITextCore,
    | "underline"
    | "strikethrough"
    | "asList"
    | "typographyStyle"
    | "textCase"
    | "alignmentX"
    | "lineHeight"
    | "italic"
    | "quote"
>;

const Context = createContext({} as TextCoreContextValue);

interface ITextCoreProvider extends PropsWithChildren, TextCoreContextValue {}

export const TextCoreProvider: FC<ITextCoreProvider> = ({
    underline,
    strikethrough,
    italic,
    quote,
    asList,
    typographyStyle = "body-l",
    textCase = "asTyped",
    alignmentX = "start",
    lineHeight,
    children,
}) => {
    const value = useMemo(() => {
        return {
            strikethrough,
            underline,
            italic,
            quote,
            asList,
            typographyStyle,
            textCase,
            alignmentX,
            lineHeight,
        };
    }, [
        strikethrough,
        underline,
        italic,
        quote,
        asList,
        typographyStyle,
        textCase,
        alignmentX,
        lineHeight,
    ]);

    // eslint-disable-next-line ok-custom-rules/check-attribute
    return <Context.Provider value={value}>{children}</Context.Provider>;
};

TextCoreProvider.displayName = "TextProvider";

export const useTextCoreStore = () => {
    const context = useContext(Context);
    if (!context) {
        throw new Error(
            "useTextCoreStore должен использоваться внутри TextCoreProvider",
        );
    }
    return context;
};
